import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppFeedback } from "../../../useAppFeedback";
import { useOfflineRead } from "@dowty/offline";
import { reads, type ReadCacheKey } from "../../../../offline";
import { fetchBeerpongRules } from "../../../../offline/fetchers";
import { RuleRow } from "../types";
import { useTranslation } from "react-i18next";

interface UseBeerpongRulesResult {
    origins: string[];
    checkedOrigins: string[];
    groupedRules: Record<string, RuleRow[]>;
    toggleOrigin: (origin: string, checked: boolean) => void;
    hasActiveFilter: boolean;
    /** True when what's on screen is the last cached rule set rather than a fresh network
     * response - i.e. this is why the beer pong rules screen isn't empty offline. */
    isFromCache: boolean;
    fetchedAt: number | undefined;
    loading: boolean;
}

export const useBeerpongRules = (): UseBeerpongRulesResult => {
    const { message } = useAppFeedback();
    const [origins, setOrigins] = useState<string[]>([]);
    const [checkedOrigins, setCheckedOrigins] = useState<string[]>([]);
    const { t } = useTranslation();

    // Read-through cache (@dowty/offline): renders the last successfully fetched rule
    // set immediately, then refreshes from the network. Previously this called the callback-style
    // PongerClient directly with nothing cached, which is exactly why the rules screen was empty
    // with no connection - see fetchBeerpongRules in src/offline/fetchers.ts for the promise-based
    // replacement.
    const { data: rules, isFromCache, fetchedAt, loading, error } = useOfflineRead<ReadCacheKey, RuleRow[]>(
        reads,
        "beerpongRules",
        fetchBeerpongRules,
        []
    );

    useEffect(() => {
        if (error) {
            console.error("Failed to load beer pong rules", error);
            message.error(t("games.details.beerpong.rulesError"));
        }
    }, [error, t, message]);

    useEffect(() => {
        const allOrigins = Array.from(new Set((rules ?? []).map((r) => r.origin))).filter(Boolean);
        setOrigins(allOrigins);
        setCheckedOrigins(allOrigins);
    }, [rules]);

    const toggleOrigin = useCallback((origin: string, checked: boolean) => {
        setCheckedOrigins((prev) =>
            checked ? [...prev, origin] : prev.filter((candidate) => candidate !== origin)
        );
    }, []);

    const groupedRules = useMemo(
        () =>
            origins.reduce((groups, origin) => {
                groups[origin] = (rules ?? []).filter((r) => r.origin === origin);
                return groups;
            }, {} as Record<string, RuleRow[]>),
        [origins, rules]
    );

    return {
        origins,
        checkedOrigins,
        groupedRules,
        toggleOrigin,
        hasActiveFilter: checkedOrigins.length > 0,
        isFromCache,
        fetchedAt,
        loading,
    };
};
